import { lazy } from 'react';
import {
  HomeOutlined,
  TableOutlined,
  DashboardOutlined,
  UserOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { MenuType, DataRouteConfig } from '@/types';

const Home = lazy(() => import('@/pages/Home'));
const TableList = lazy(() => import('@/pages/TableList'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const UserManagement = lazy(() => import('@/pages/Admin/UserManagement'));

// Route configurations using React Router's data API
// Note: CAD Editor is now at root path '/' and excluded from admin routes
export const dataRoutes: DataRouteConfig[] = [
  {
    id: 'home',
    path: '/',
    title: 'Home',
    icon: <HomeOutlined />,
    Component: Home,
  },
  {
    id: 'table-list',
    path: '/table-list',
    title: 'Table List',
    icon: <TableOutlined />,
    Component: TableList,
    noMenu: true,
  },
  {
    id: 'dashboard',
    path: '/dashboard',
    title: 'Dashboard',
    icon: <DashboardOutlined />,
    Component: Dashboard,
    noMenu: true,
  },
  {
    id: 'users',
    path: '/users',
    title: 'Users',
    icon: <UserOutlined />,
    Component: UserManagement,
    noMenu: true,
  },
  {
    id: 'contact-us',
    path: '/contact-us',
    title: 'Contact Us',
    icon: <MailOutlined />,
    Component: Home,
    redirect: '/landing',
  },
];

// Helper function to extract menu structure from data routes for Menu component
export const extractMenuStructure = (routes: DataRouteConfig[]): MenuType[] => {
  const menuMap = new Map<string, MenuType>();

  routes.forEach((route) => {
    // Skip routes without titles (they won't be displayed in menu)
    if (!route.title) return;

    const pathParts = route.path?.split('/').filter(Boolean) || [];

    if (pathParts.length === 0) {
      // Root level route
      menuMap.set(route.path!, {
        path: route.path!,
        title: route.title,
        icon: route.icon,
        component: route.Component,
        noMenu: route.noMenu,
        hasPermission: route.hasPermission,
        redirect: route.redirect,
      });
    } else if (pathParts.length === 1) {
      // First level route
      const existing = menuMap.get(`/${pathParts[0]}`);
      if (!existing) {
        menuMap.set(`/${pathParts[0]}`, {
          path: `/${pathParts[0]}`,
          title: route.title,
          icon: route.icon,
          component: route.Component,
          noMenu: route.noMenu,
          hasPermission: route.hasPermission,
          redirect: route.redirect,
        });
      }
    } else if (pathParts.length === 2) {
      // Second level route (submenu)
      const parentPath = `/${pathParts[0]}`;
      const parent = menuMap.get(parentPath);

      if (parent) {
        if (!parent.subMenu) {
          parent.subMenu = [];
        }
        parent.subMenu.push({
          path: route.path!,
          title: route.title,
          icon: route.icon,
          component: route.Component,
          noMenu: route.noMenu,
          hasPermission: route.hasPermission,
          redirect: route.redirect,
        });
      } else {
        // Create parent if it doesn't exist
        menuMap.set(parentPath, {
          path: parentPath,
          title: pathParts[0],
          icon: <HomeOutlined />,
          subMenu: [
            {
              path: route.path!,
              title: route.title,
              icon: route.icon,
              component: route.Component,
              noMenu: route.noMenu,
              hasPermission: route.hasPermission,
              redirect: route.redirect,
            },
          ],
        });
      }
    }
  });

  return Array.from(menuMap.values());
};
